import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

/**
 * Fixed-position anchoring for a portaled dropdown panel (Unit 3 — dropdown
 * clipping fix). The panel is rendered in a portal to `document.body` so it
 * escapes any `overflow-hidden`/`overflow-y-auto` ancestor (Action View's card
 * clipped the menu on all sides). Position is computed from the trigger's
 * viewport rect:
 *
 *  - default: anchor the menu's LEFT edge to the trigger's left (open rightward)
 *  - flip to right-align if left-anchoring would overflow the viewport right
 *  - flip upward if opening below would overflow the bottom (and there's room)
 *  - clamp to the viewport with a small margin
 *  - a viewport-relative maxHeight/maxWidth constrains a menu that grows
 *    after opening (e.g. content loads in) — internal scroll, not overflow
 *  - if the TRIGGER itself scrolls fully out of view, the menu closes
 *    outright (fix #6) rather than clamping a detached panel to a corner,
 *    which would stay clickable with no visible anchor (wrong-contact risk)
 */

export interface RectLike {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface AnchorInput {
  trigger: RectLike;
  menu: { width: number; height: number };
  viewport: { width: number; height: number };
  /** Viewport edge padding. Default 8. */
  margin?: number;
  /** Vertical gap between trigger and menu. Default 4. */
  gap?: number;
  /**
   * Horizontal anchor side (Unit 3 dropdown-positioning-logic promotion, D9).
   * Default `'left'` — anchor the menu's left edge to the trigger's left,
   * flipping to right-align only if that would overflow the viewport (the
   * original, unchanged behavior). `'right'` is for a menu whose trigger is
   * the right-most column in a table row (`PlayRowActionsMenu`,
   * `CampaignRowActionsMenu`): it ALWAYS starts right-aligned
   * (`trigger.right - menu.width`) — there is no left-anchor attempt to flip
   * away from, since left-aligning the last column's menu would routinely
   * run it off the right edge of the viewport instead.
   */
  align?: 'left' | 'right';
}

/** Pure flip/clamp geometry — unit-tested in isolation. */
export function computeAnchoredPosition(input: AnchorInput): { top: number; left: number; maxHeight: number; maxWidth: number } {
  const margin = input.margin ?? 8;
  const gap = input.gap ?? 4;
  const align = input.align ?? 'left';
  const { trigger, menu, viewport } = input;

  let left: number;
  if (align === 'right') {
    // Always right-align — no left-anchor attempt to flip away from.
    left = trigger.right - menu.width;
  } else {
    // Horizontal — default: left-align to the trigger (open rightward).
    left = trigger.left;
    // Flip to right-align if left-anchoring overflows the viewport right edge.
    if (left + menu.width > viewport.width - margin) {
      left = trigger.right - menu.width;
    }
  }
  // Clamp within the viewport (never let the menu run off either edge).
  left = Math.max(margin, Math.min(left, viewport.width - menu.width - margin));

  // Vertical — default: just below the trigger.
  let top = trigger.bottom + gap;
  if (top + menu.height > viewport.height - margin) {
    const above = trigger.top - gap - menu.height;
    // Flip upward only if the menu fits fully above; otherwise clamp.
    top = above >= margin ? above : Math.max(margin, viewport.height - menu.height - margin);
  }
  top = Math.max(margin, top);

  // Viewport-relative size cap (fix #6): a menu that grows after opening
  // (definitions/plays loading in) is constrained with internal scroll
  // instead of running off-screen — the caller applies these as CSS
  // `maxHeight`/`maxWidth` + `overflow: auto`.
  const maxHeight = viewport.height - margin * 2;
  const maxWidth = viewport.width - margin * 2;

  return { top, left, maxHeight, maxWidth };
}

/**
 * True when the trigger has scrolled completely out of the viewport (no
 * overlap in any direction) — e.g. the docked sidebar or a scrollable table
 * scrolled the trigger fully above/below/left/right. A positioned menu with
 * no visible anchor is a wrong-contact-click risk, so the caller closes the
 * menu outright in this case rather than clamping a detached panel to a
 * corner (fix #6).
 */
export function isAnchorOutOfView(trigger: RectLike, viewport: { width: number; height: number }): boolean {
  // A zero-size rect pinned at the origin is what an UNMEASURED element
  // reports (jsdom always returns this shape; a real browser can too for a
  // brief instant before first layout/paint) — treat it as "not yet
  // measured," never "scrolled out of view." Without this guard, `bottom <=
  // 0` is trivially true for `{top:0,left:0,right:0,bottom:0}`, so a menu
  // would self-close the instant it opens in any environment where layout
  // hasn't run yet (this is exactly what jsdom returns for every element by
  // default, breaking every dropdown test).
  if (trigger.width === 0 && trigger.height === 0 && trigger.top === 0 && trigger.left === 0) return false;
  return trigger.bottom <= 0 || trigger.top >= viewport.height || trigger.right <= 0 || trigger.left >= viewport.width;
}

const HIDDEN_STYLE: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden', zIndex: 60 };

/**
 * Computes a fixed-position `style` for an open, portaled menu panel and keeps
 * it in sync on scroll (capture — catches scroll in ANY ancestor), window
 * resize, AND size changes of the trigger or menu itself (ResizeObserver —
 * fix #6: a menu that opens small (loading) and then grows once content
 * loads in must be re-measured, or it can run off-screen with stale
 * geometry). If the trigger scrolls fully out of the viewport, `onClose` is
 * invoked and the menu is hidden rather than clamped to a corner with no
 * visible anchor (fix #6 — wrong-contact click risk).
 *
 * StrictMode-resilient: the effect holds no lifetime refs to reset — it
 * recomputes from scratch on every open and tears down all listeners/
 * observers in cleanup, so the dev double-invoke (setup→cleanup→setup) is a
 * no-op net. `onClose` is read through a ref updated every render (not put in
 * the effect's dependency array) — callers typically pass an inline
 * `() => setOpen(false)`, and putting a re-created-every-render function in
 * the deps would tear down and re-run this effect (and thus call `update()`)
 * on every parent re-render, which is unnecessary churn; a plain ref-sync (no
 * subscriptions, StrictMode-safe by construction) avoids it without requiring
 * callers to memoize.
 */
export function useAnchoredPosition(
  triggerRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
  open: boolean,
  onClose?: () => void,
  align?: 'left' | 'right',
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const alignRef = useRef(align);
  alignRef.current = align;

  useLayoutEffect(() => {
    if (!open) {
      // Reset to hidden so the next open measures fresh rather than flashing at
      // the previous position.
      setStyle(HIDDEN_STYLE);
      return;
    }
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const update = () => {
      const t = trigger.getBoundingClientRect();
      const viewport = { width: window.innerWidth, height: window.innerHeight };

      if (isAnchorOutOfView(t, viewport)) {
        // The trigger itself has scrolled entirely out of view — a
        // positioned menu here would float over unrelated content with no
        // visible anchor. Close outright rather than clamp a detached panel.
        setStyle(HIDDEN_STYLE);
        onCloseRef.current?.();
        return;
      }

      const m = menu.getBoundingClientRect();
      const { top, left, maxHeight, maxWidth } = computeAnchoredPosition({
        trigger: t,
        menu: { width: m.width, height: m.height },
        viewport,
        align: alignRef.current,
      });
      setStyle({
        position: 'fixed', top, left, visibility: 'visible', zIndex: 60,
        maxHeight, maxWidth, overflowY: 'auto', overflowX: 'hidden',
      });
    };

    update();
    // Capture-phase scroll so a scroll in any overflow ancestor repositions us.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    // Re-measure when the trigger OR the menu itself changes size — the menu
    // commonly opens small ("Loading…") and grows once content resolves.
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(trigger);
    resizeObserver.observe(menu);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      resizeObserver.disconnect();
    };
  }, [open, triggerRef, menuRef]);

  return style;
}
